import { config } from '@abyss/web/tools/config';
import { find } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { adobeModalTrackEvent } from '../../../common/AdobeTagging/adobeModalTrackEvent';
import { getFeatureFlag } from '../../../common/Utils';
import { isUmrMember } from '../../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../../context/PortalContext';
import { useLagoon } from '../../../hooks/useLagoon';
import { useChipStore } from '../../../store/useChipStore';
import { ChipState } from '../../../store/useChipStore/chipStore';
import { useTypeaheadStore } from '../../../store/useTypeaheadStore';
import { TypeaheadState } from '../../../store/useTypeaheadStore/typeaheadStore';
import { adobeLinkTrackEvent } from '../../AdobeTagging/adobeLinkTrackEvent';
import { CareCategories, SectionType } from '../../Constants';
import { ConstantsLagoon } from '../../ConstantsLagoon';
import { FAQLink } from '../../SmartChoiceBreakDownDrawer/SmartChoiceBreakDown.styles';
import {
  StyledContentWrapper,
  StyledFAQContainer,
  StyledSetPreferencesButton,
  StyledSmartChoiceMessage,
  StyledSmartChoiceText,
  drawerStyles,
} from './NonSmartChoiceOnboarding.styled.';
import { StyledDrawer } from './SmartChoice.styled';

export interface Props {
  isOpen: boolean;
  handleCloseDrawer: () => void;
  handleOpenSetPreferencesModal: () => void;
}

const getChipToLagoonKeyMap = (enableSmartChoiceForFacilities: boolean) => {
  const baseMap = {
    [CareCategories.HEALTH_SUPPLIES]:
      ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
        .SMART_CHOICE_UNAVAILABLE_FOR_HEALTH_SUPPLIES,
    [CareCategories.VISION]:
      ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
        .SMART_CHOICE_UNAVAILABLE_FOR_VISION,
    [CareCategories.DENTAL]:
      ConstantsLagoon.UI_MESSAGING.SMART_CHOICE
        .SMART_CHOICE_UNAVAILABLE_FOR_DENTAL,
    [CareCategories?.BEHAVIORAL_HEALTH]:
      ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SMART_CHOICE_UNAVAILABLE_FOR_BH,
  };

  // Only include FACILITIES in unavailable map if feature flag is OFF
  if (!enableSmartChoiceForFacilities) {
    baseMap[CareCategories.FACILITIES] =
      ConstantsLagoon.UI_MESSAGING.SMART_CHOICE.SMART_CHOICE_UNAVAILABLE_FOR_FACILITIES;
  }

  return baseMap;
};

const getSmartChoiceMessage = (
  chipName: string,
  lagoonData: any,
  enableSmartChoiceForFacilities: boolean
) => {
  const chipToLagoonKeyMap = getChipToLagoonKeyMap(
    enableSmartChoiceForFacilities
  );
  const lagoonKey = chipToLagoonKeyMap[chipName];
  if (!lagoonKey) {
    return null;
  }
  const message = find(lagoonData, { key: lagoonKey });
  return message?.message;
};

export const NonSmartChoiceOnboarding = ({
  isOpen,
  handleCloseDrawer,
  handleOpenSetPreferencesModal,
}: Props) => {
  const { portalData } = useContext(PortalContext);
  const lagoonData = useLagoon('ui-messaging')();
  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();
  const { chipValue } = useChipStore(useShallow((state: ChipState) => state));

  const enableSmartChoiceForFacilities = getFeatureFlag(
    featureFlags,
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_PSX_FACILITIES_RECOMMENDATIONS
  );

  const smartChoiceMessage = getSmartChoiceMessage(
    chipValue,
    lagoonData,
    enableSmartChoiceForFacilities
  );
  const isUmr = isUmrMember(portalData?.portalName);
  const baseUrl = isUmr ? config('UMR_BASE_URL') : config('BASE_URL');
  const smartChoice = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE?.SMART_CHOICE,
  });

  const smartChoiceAvailableForPrimaryAndMedicalStyled = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE
      ?.SMART_CHOICE_AVAILABLE_ONLY_FOR_PRIMARY_MEDICAL_STYLED,
  });

  const smartChoiceUnavailableForThisSearch = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE
      ?.SMART_CHOICE_UNAVAILABLE_FOR_THIS_SEARCH,
  });

  const smartChoiceUnavailableForFacilitiesSearch = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE
      ?.SMART_CHOICE_UNAVAILABLE_FOR_FACILITIES_SEARCH,
  });

  const smartChoiceUnavailableForProviderGroupSearch = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE
      ?.SMART_CHOICE_UNAVAILABLE_FOR_PROVIDER_GROUP_SEARCH,
  });

  const SC_ONBOARDING_FAQ_LINK = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE?.SC_ONBOARDING_FAQ_LINK,
  });

  const continueToPreferences = find(lagoonData, {
    key: ConstantsLagoon?.UI_MESSAGING?.SMART_CHOICE
      ?.SMART_CHOICE_CONTINUE_TO_PREFERENCES,
  });

  const enableAdobeAnalytic = getFeatureFlag(
    featureFlags,
    ConstantsLagoon?.FEATURE_FLAGS.ENABLE_NEW_UX_SMART_CHOICE_ANALYTICS
  );

  // Check if the current page is the Typeahead Search Page
  const { search, sectionType } = useTypeaheadStore(
    useShallow((state: TypeaheadState) => ({
      search: state?.typeaheadSearchStore.search,
      sectionType: state?.typeaheadSearchStore.sectionType,
    }))
  );
  function getSmartChoiceUnavailableMessage() {
    switch (sectionType?.toLowerCase()) {
      case SectionType?.FACILITY?.toLowerCase():
        // If user selected Facilities chip AND feature flag is enabled, Smart Choice is available
        return enableSmartChoiceForFacilities &&
          chipValue === CareCategories.FACILITIES
          ? ''
          : smartChoiceUnavailableForFacilitiesSearch?.message;
      case SectionType?.PROVIDER_GROUP?.toLowerCase():
        return smartChoiceUnavailableForProviderGroupSearch?.message;
      case SectionType?.PROVIDER?.toLowerCase():
        return smartChoiceUnavailableForThisSearch?.message;
      default:
        return '';
    }
  }
  const unavailableMessage = getSmartChoiceUnavailableMessage();
  const unavailableTxt = search ? unavailableMessage : smartChoiceMessage;
  const faqLink = `${baseUrl}/findcare/faq`;

  useEffect(() => {
    if (!isOpen || !smartChoice?.title || !enableAdobeAnalytic) return;
    adobeModalTrackEvent(smartChoice?.title);
  }, [isOpen]);

  const onClickSetPreferences = () => {
    if (enableAdobeAnalytic) {
      adobeLinkTrackEvent({
        name: continueToPreferences?.message,
        location: smartChoice?.title,
      });
    }
    handleCloseDrawer();
    handleOpenSetPreferencesModal();
  };

  const handleFaqClick = () => {
    if (enableAdobeAnalytic) {
      adobeLinkTrackEvent({
        name: SC_ONBOARDING_FAQ_LINK?.message,
        location: smartChoice?.title,
        destinationUrl: faqLink,
      });
    }
    handleCloseDrawer();
  };

  const handleOnClose = () => {
    if (enableAdobeAnalytic) {
      adobeLinkTrackEvent({
        name: 'close',
        location: smartChoice?.title,
      });
    }
    handleCloseDrawer();
  };

  return (
    <React.Fragment>
      <StyledDrawer
        data-testid="non-smart-choice-onboarding-drawer"
        isOpen={isOpen}
        onClose={handleOnClose}
        position={
          drawerStyles.drawerPosition as
            | 'bottom'
            | 'left'
            | 'right'
            | 'top'
            | 'fullscreen'
        }
        size={drawerStyles.drawerSize}
        title={smartChoice?.title}
      >
        <StyledContentWrapper data-testid="non-smart-choice-content-wrapper">
          <StyledSmartChoiceText data-testid="non-smart-choice-text">
            {unavailableTxt}
          </StyledSmartChoiceText>
          <StyledSmartChoiceMessage
            dangerouslySetInnerHTML={{
              __html: smartChoiceAvailableForPrimaryAndMedicalStyled?.message,
            }}
            data-testid="non-smart-choice-message"
          />
          <StyledSetPreferencesButton
            data-testid="non-sc-set-preferences-button"
            onClick={onClickSetPreferences}
          >
            {continueToPreferences?.message}
          </StyledSetPreferencesButton>
          <StyledFAQContainer data-testid="non-smart-choice-faq-container">
            <FAQLink
              data-testid="non-sc-faq-link"
              href={faqLink}
              onClick={handleFaqClick}
            >
              {SC_ONBOARDING_FAQ_LINK?.message}
            </FAQLink>
          </StyledFAQContainer>
        </StyledContentWrapper>
      </StyledDrawer>
    </React.Fragment>
  );
};
